import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Modal,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

// ── Helpers ─────────────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const hashColor = (str = '') => {
  // Deterministic colour from username so each user always gets the same avatar bg.
  const palette = ['#7E57C2', '#5C6BC0', '#42A5F5', '#26A69A', '#EF5350', '#FFA726', '#AB47BC', '#66BB6A'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
};

// ── Page ────────────────────────────────────────────────────────────
const ProfilePage = () => {
  const { user, changePassword, requestEmailChange } = useAuth();
  const [tab, setTab] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  if (!user) return <Typography sx={{ p: 4 }}>Loading…</Typography>;

  const displayName = user.full_name || user.username || 'User';
  const initials = getInitials(displayName);
  const avatarColor = hashColor(user.username || displayName);

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      {/* Compact header strip */}
      <Stack direction="row" alignItems="center" spacing={2} mb={4}>
        <Avatar sx={{ bgcolor: avatarColor, width: 56, height: 56, fontSize: 22, fontWeight: 600 }}>
          {initials}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
        {user.role && (
          <Chip label={user.role} size="small" sx={{ textTransform: 'capitalize' }} />
        )}
      </Stack>

      {/* Real tabs — only one section visible at a time */}
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Account" />
          <Tab label="Change Email" />
          <Tab label="Change Password" />
          <Tab label="Notifications" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tab === 0 && <AccountTab user={user} displayName={displayName} />}
          {tab === 1 && <ChangeEmailTab requestEmailChange={requestEmailChange} />}
          {tab === 2 && <ChangePasswordTab changePassword={changePassword} />}
          {tab === 3 && <NotificationsTab />}
        </Box>
      </Paper>

      {/* Danger zone — compact row, distinct via red text + button only */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mt: 4, px: 1 }}
      >
        <Box>
          <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
            Delete account
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Permanent — removes all fall events, cameras and history.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete…
        </Button>
      </Stack>

      <DeleteAccountModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      />
    </Container>
  );
};

// ── Tab content components ──────────────────────────────────────────
const AccountTab = ({ user, displayName }) => (
  <Stack spacing={2.5}>
    <DetailRow label="Name" value={displayName} />
    <DetailRow label="Username" value={user.username} />
    <DetailRow label="Email" value={user.email} />
    {user.role && <DetailRow label="Role" value={user.role} capitalise />}
  </Stack>
);

const DetailRow = ({ label, value, capitalise }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography
      variant="body1"
      sx={{ fontWeight: 500, textTransform: capitalise ? 'capitalize' : 'none' }}
    >
      {value || '—'}
    </Typography>
  </Stack>
);

const ChangeEmailTab = ({ requestEmailChange }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    const result = await requestEmailChange({ currentPassword, newEmail });
    setLoading(false);
    setStatus(result.success
      ? { severity: 'success', message: result.message || 'Confirmation link sent.' }
      : { severity: 'error', message: result.message || 'Unable to request change.' });
    if (result.success) {
      setCurrentPassword('');
      setNewEmail('');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {status && <Alert severity={status.severity}>{status.message}</Alert>}
        <TextField
          label="Current password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="New email address"
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          required
          fullWidth
        />
        <Box>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Sending…' : 'Send confirmation link'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          We'll send a confirmation link to the new address. The change takes effect once you click it.
        </Typography>
      </Stack>
    </Box>
  );
};

const ChangePasswordTab = ({ changePassword }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setStatus({ severity: 'error', message: 'New passwords do not match.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    const result = await changePassword({ currentPassword, newPassword, confirmPassword });
    setLoading(false);
    setStatus(result.success
      ? { severity: 'success', message: result.message || 'Password updated.' }
      : { severity: 'error', message: result.message || 'Unable to change password.' });
    if (result.success) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {status && <Alert severity={status.severity}>{status.message}</Alert>}
        <TextField
          label="Current password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="New password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          fullWidth
        />
        <Box>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Updating…' : 'Update password'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

const NotificationsTab = () => {
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(false);

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Choose how you'd like to be notified about new fall events.
      </Typography>
      <FormControlLabel
        control={<Switch checked={email} onChange={(e) => setEmail(e.target.checked)} />}
        label="Email notifications"
      />
      <FormControlLabel
        control={<Switch checked={sms} onChange={(e) => setSms(e.target.checked)} />}
        label="SMS alerts (charges may apply)"
      />
      <Divider />
      <Box>
        <Button variant="contained" disabled>
          Save preferences
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          (Backend endpoint not yet wired)
        </Typography>
      </Box>
    </Stack>
  );
};

const DeleteAccountModal = ({ open, onClose }) => (
  <Modal open={open} onClose={onClose}>
    <Paper
      sx={{
        position: 'absolute',
        top: '30%',
        left: '50%',
        transform: 'translate(-50%, 0)',
        width: 420,
        p: 3,
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" color="error" gutterBottom>
        Delete account?
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        This is permanent. All linked data — fall events, cameras, history — will be removed
        and cannot be recovered.
      </Typography>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error">
          Yes, delete
        </Button>
      </Stack>
    </Paper>
  </Modal>
);

export default ProfilePage;
